import React, { FC, PropsWithChildren, ReactNode } from 'react'
import Sticky from 'react-stickynode'
import classnames from 'classnames'
import { LeftArrow } from 'components/atoms/icons'
import moment from 'moment'
import IconButton from 'transfix-ui/components/IconButton'
import { Spinner } from 'transfix-ui/components/Spinner'
import { ErrorMessage } from 'components/ErrorBoundary'
import { CloseIcon } from 'transfix-icons'
import { FORMATS } from 'constants/datetime'
import 'scss/5_components/_sidebar.scss'

interface IInformation {
  lastActionAt?: string
  lastActionUpdatedByName?: string
}

interface ISidebarInfoProps {
  actionButtons: ReactNode
  ceTools?: boolean
  withAlerts?: string
  subtitle: ReactNode
  information?: IInformation
  closeSidebar?: () => void
}

const SidebarInfo: FC<ISidebarInfoProps> = props => {
  const { actionButtons, ceTools, closeSidebar, information, subtitle, withAlerts } = props
  const actionsClasses = ['sidebar-actions']
  if (withAlerts) {
    actionsClasses.push('sidebar-alerts')
    actionsClasses.push(withAlerts)
  }
  const actionsClassname = actionsClasses.join(' ')
  const { lastActionAt, lastActionUpdatedByName } = information || {}
  const formattedLastAction = lastActionAt
    ? moment(lastActionAt).format(FORMATS.datetimeInputFormat)
    : null

  return (
    <div className='sidebar-actions-container'>
      <div className={actionsClassname}>
        <div className='flex-grow-2'>{subtitle}</div>
        <div className='flex content-end items-center pr2'>
          {!ceTools && actionButtons}
          {!ceTools && actionButtons && <span className='mr4' />}
          {(lastActionAt || lastActionUpdatedByName) && (
            <span className='mr4 text-caption'>
              Last updated by {lastActionUpdatedByName} {formattedLastAction}
            </span>
          )}
          {closeSidebar && (
            <IconButton data-test='sidebar-close-button' Icon={CloseIcon} onClick={closeSidebar} />
          )}
        </div>
      </div>
    </div>
  )
}

export interface ISidebarProps {
  /**
   * @default 'sidebar'
   */
  dataTest?: string
  withAlerts?: string
  actionButtons: ReactNode
  subtitle: ReactNode
  error?: string
  /**
   * @default false
   */
  open?: boolean
  /**
   * @default false
   */
  loading?: boolean
  closeSidebar?: () => void
  information?: IInformation
  expandableContent: ReactNode
  /**
   * @default false
   */
  expanded?: boolean
  setExpanded: (expanded: boolean) => void
}

export const Sidebar: FC<PropsWithChildren<ISidebarProps>> = props => {
  const {
    actionButtons,
    children,
    closeSidebar,
    dataTest = 'sidebar',
    error,
    expandableContent,
    expanded = false,
    information,
    loading = false,
    open = false,
    setExpanded,
    subtitle,
    withAlerts,
  } = props
  const containsExpandableContent = expandableContent
  const hasContentAndExpanded = containsExpandableContent && expanded
  const toggleExpanded = () => {
    setExpanded(!expanded)
  }

  const mainSidebarClass = classnames(
    { 'col-xs-4': hasContentAndExpanded },
    { 'flex-auto': containsExpandableContent }
  )
  const expandableContainerClass = `expandable ${
    hasContentAndExpanded ? 'col-xs-8' : 'expandable-toggle tc'
  }`
  const arrowContainerClass = `arrow ${hasContentAndExpanded ? 'fr arrow-expanded' : ''}`
  const arrowClass = `mv6 rotate-transition ${hasContentAndExpanded ? 'rotate' : ''}`

  const headerText = loading ? <Spinner position='relative' /> : subtitle
  const classes = [
    'card',
    'sidebar',
    containsExpandableContent ? 'flex row collapse no-gutter' : '',
  ]
  if (open) classes.push('active')
  return (
    <Sticky enabled={open} top={48} enableTransforms={false}>
      <section data-test={dataTest} className={classes.join(' ')}>
        {containsExpandableContent && (
          <div className={expandableContainerClass}>
            <div
              aria-label='Expand sidebar'
              onClick={toggleExpanded}
              onKeyPress={toggleExpanded}
              role='button'
              tabIndex={0}
              className={arrowContainerClass}
              data-test='expandable-arrow'>
              <LeftArrow className={arrowClass} />
            </div>
            {hasContentAndExpanded && expandableContent}
          </div>
        )}
        <div className={mainSidebarClass}>
          <SidebarInfo
            subtitle={headerText}
            actionButtons={loading ? null : actionButtons}
            closeSidebar={closeSidebar}
            withAlerts={withAlerts}
            information={information}
          />
          <section>
            {!loading && error && <ErrorMessage />}
            {!loading && !error && children}
          </section>
        </div>
      </section>
    </Sticky>
  )
}
